export type MojibakeKind = "fffd_halfkana" | "fffd_only" | "double_q" | "fullwidth_q" | "q_in_nonascii";

const FFFD = "�";
const FFFD_HALFKANA_RE = /�[･-ﾟ]/;
const FULLWIDTH_Q_RE = /？/;
const DOUBLE_Q_RE = /\?\?/;
const SINGLE_Q_RE = /\?/;
const NON_ASCII_RE = /[^\x01-\x7F]/;

export function detectMojibakeKind(t: string | null | undefined): MojibakeKind | null {
  if (!t) return null;
  if (FFFD_HALFKANA_RE.test(t)) return "fffd_halfkana";
  if (t.indexOf(FFFD) >= 0) return "fffd_only";
  if (FULLWIDTH_Q_RE.test(t)) return "fullwidth_q";
  if (DOUBLE_Q_RE.test(t)) return "double_q";
  if (SINGLE_Q_RE.test(t) && NON_ASCII_RE.test(t)) return "q_in_nonascii";
  return null;
}

// 化けた 1 字。「�＋半角カナ」「�＋半角英数記号（0x40〜0x7E）」は 1 字（�ｱ＝﨑・�M＝濱 など。失われた 1 バイト目の後ろに
// 2 バイト目が残ったもの）。「���ｱ」は「��」と「�ｱ」の 2 字（髙﨑 など）なので、�の連続は後ろの 1 個を残して切る
const MOJIBAKE_TOKEN_RE = /�[･-ﾟ\x40-\x7E]|�+(?![･-ﾟ\x40-\x7E])|[?？]+/gu;
// 埋めてよい字（漢字・かな・英字）。顧客の氏名側の「●口」のような伏せ字は写さない
const FILLABLE_CHAR_RE = /^[一-鿿㐀-䶿豈-﫿\u{20000}-\u{2EBEF}ぁ-ゖァ-ヺーA-Za-zＡ-Ｚａ-ｚ]$/u;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * 名刺の化けた氏名 card を、化けていない顧客の氏名 customer で埋めた値を返す。埋められなければ null。
 * 化けた部分を 1 字とみなし、それ以外の字が空白を除いて顧客の氏名と完全に一致するときだけ、
 * 化けた部分を顧客の氏名の対応する 1 字に置き換える。名刺側の空白の位置はそのまま残す。
 * scripts/bizcard-mojibake-repair の pg_temp.mojibake_name_fix() と同じ規則（#100）。
 */
export function fillMojibakeName(card: string | null | undefined, customer: string | null | undefined): string | null {
  if (!card || !customer || detectMojibakeKind(card) === null || detectMojibakeKind(customer) !== null) return null;
  const literals = card.replace(/\s+/g, "").split(MOJIBAKE_TOKEN_RE);
  const pattern = new RegExp(`^${literals.map(escapeRegExp).join("(.)")}$`, "u");
  const captures = customer.replace(/\s+/g, "").match(pattern)?.slice(1);
  if (!captures || !captures.every((c) => FILLABLE_CHAR_RE.test(c))) return null;
  const parts = card.split(MOJIBAKE_TOKEN_RE);
  return parts.reduce((acc, part, i) => acc + captures[i - 1] + part);
}

export const MOJIBAKE_HINTS: Record<string, string[]> = {
  "��": ["髙", "高"],
  "�ｱ": ["﨑", "崎"],
  "??": ["𠮷", "吉"],
  "�ｺ": ["德", "徳"],
  "�P": ["瀨", "瀬"],
  "�M": ["濱", "浜"],
  "�ｪ": ["寬", "寛"],
};

export function suggestKanji(mojibakeText: string): string[] {
  const suggestions = new Set<string>();
  for (const [pattern, candidates] of Object.entries(MOJIBAKE_HINTS)) {
    if (mojibakeText.includes(pattern)) {
      for (const candidate of candidates) {
        suggestions.add(mojibakeText.split(pattern).join(candidate));
      }
    }
  }
  return Array.from(suggestions);
}

export const MOJIBAKE_KIND_LABELS: Record<MojibakeKind, string> = {
  fffd_halfkana: "U+FFFD + 半角カナ",
  fffd_only: "U+FFFD 単体",
  double_q: "?? (2 連続)",
  fullwidth_q: "？ (全角)",
  q_in_nonascii: "? (非 ASCII 混在)",
};
